import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, Target, Calculator, TrendingUp, Shield,
  Wallet, RefreshCw, CreditCard, ShoppingBag, Check, Smartphone,
} from 'lucide-react';
import CompanyHeader from '../components/CompanyHeader';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
};

const MotionLink = motion(Link);

// ─── Tools catalogue ──────────────────────────────────────────────────────────
const tools = [
  {
    icon: Target,
    label: 'Financial Goal Planner',
    tag: 'Free',
    available: true,
    description: 'Set savings targets, record contributions, and track every deposit toward the things that matter most.',
    features: ['Goal tracking across all your savings', 'M-Pesa & bank contributions', 'Visual progress with target dates'],
    to: '/financial-goal-planner',
    cta: 'Explore tool',
  },
  {
    icon: Calculator,
    label: 'Compound Calculator',
    tag: 'Free',
    available: true,
    description: 'See exactly how compound interest grows your money over time — with or without regular top-ups.',
    features: ['Monthly & yearly compounding', 'Top-up frequency simulation', 'PDF report sent to your email'],
    to: '/app/calculator',
    cta: 'Try it now',
  },
  {
    icon: Wallet,
    label: 'Budget Planner',
    tag: 'PRO',
    available: true,
    description: 'Plan your month, track every shilling spent, and instantly see your surplus or deficit by category.',
    features: ['Income & expense categories', 'Budget vs actual variance', 'Month-over-month history'],
    to: '/app/dashboard',
    cta: 'Open dashboard',
  },
  {
    icon: CreditCard,
    label: 'Debt Payoff Tracker',
    tag: 'PRO',
    available: true,
    description: 'Eliminate debt faster using avalanche or snowball strategies. See your debt-free date at a glance.',
    features: ['Avalanche & snowball strategies', 'Payoff date projection', 'Payment log per lender'],
    to: '/app/dashboard',
    cta: 'Open dashboard',
  },
  {
    icon: TrendingUp,
    label: 'Investment Tracker',
    tag: 'PRO',
    available: true,
    description: 'Track your portfolio across NSE stocks, crypto, SACCO shares, and bonds in one clean dashboard.',
    features: ['Multi-asset portfolio view', 'Live gain / loss per holding', 'Allocation breakdown'],
    to: '/app/dashboard',
    cta: 'Open dashboard',
  },
  {
    icon: RefreshCw,
    label: 'Recurring Transactions',
    tag: 'PRO',
    available: true,
    description: 'Log every standing order, subscription, and salary deposit once — never miss a recurring cash flow.',
    features: ['Income & expense recurring', 'Daily / weekly / monthly / yearly', 'Smart reminders'],
    to: '/app/dashboard',
    cta: 'Open dashboard',
  },
  {
    icon: ShoppingBag,
    label: 'Shop',
    tag: 'Shop',
    available: true,
    description: 'Buy ready-made Google Sheets financial templates. Pay once via M-Pesa, download instantly.',
    features: ['14 templates across 4 categories', 'Real formulas, charts, and dashboards', 'From KES 599'],
    to: '/shop',
    cta: 'Browse templates',
    highlight: true,
  },
  {
    icon: Shield,
    label: 'Financial Estate Planner',
    tag: 'Coming soon',
    available: false,
    description: 'Structure and document your estate so your wealth is protected and transferred as you intend.',
    features: ['Beneficiary management', 'Asset inventory', 'Document checklist'],
    to: null,
    cta: null,
  },
];

// ─── Template Shop preview items ──────────────────────────────────────────────
const shopPreviews = [
  { sku: 'BUDGET_ALLINONE_2026', name: 'All-in-One Budget + Debt + Savings Planner 2026', category: 'Personal Finance', price: 799,  original: 1299 },
  { sku: 'BIZ_BOOKKEEPING',      name: 'Business Bookkeeping Spreadsheet',                category: 'Business',        price: 699,  original: 1099 },
  { sku: 'PORTFOLIO_TRACKER',    name: 'Stocks, Crypto & SACCO Portfolio Tracker',        category: 'Investment',      price: 699,  original: 1099 },
  { sku: 'CHAMA_TRACKER',        name: 'Chama / Investment Group Tracker',                category: 'Investment',      price: 699,  original: 1099 },
  { sku: 'DEBT_PAYOFF',          name: 'Debt Payoff Planner',                             category: 'Personal Finance', price: 599, original: 999  },
  { sku: 'FREELANCER_INVOICE',   name: 'Freelancer Invoice & Income Tracker',             category: 'Business',        price: 699,  original: 999  },
];

const categoryColor: Record<string, string> = {
  'Personal Finance': 'bg-teal/10 text-teal',
  'Business':         'bg-ink/10 dark:bg-white/10 text-ink-60 dark:text-ink-20',
  'Investment':       'bg-mint/20 text-ink dark:text-paper',
  'Lifestyle':        'bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-400',
};

// ─── Pricing tiers ────────────────────────────────────────────────────────────
const tiers = [
  {
    name: 'Free',
    price: 'KES 0',
    period: 'forever',
    desc: 'Get started with core tools, no card needed.',
    features: [
      'Financial Goal Planner',
      'Compound Calculator',
      'Mobile app (iOS & Android)',
      'M-Pesa contribution logging',
    ],
    cta: 'Get started free',
    ctaTo: '/register',
    highlight: false,
  },
  {
    name: 'PRO',
    price: 'KES 299',
    period: 'per month',
    desc: 'The full suite for anyone serious about their finances.',
    features: [
      'Everything in Free',
      'Budget Planner',
      'Debt Payoff Tracker',
      'Investment Portfolio',
      'Recurring Transactions',
      'Unlimited goals & history',
      'Priority support',
    ],
    cta: 'Upgrade to PRO',
    ctaTo: '/register',
    highlight: true,
  },
  {
    name: 'Templates',
    price: 'From KES 599',
    period: 'one-time',
    desc: 'Buy individual Google Sheets templates. No subscription.',
    features: [
      '14 templates across 4 categories',
      'Real formulas & embedded charts',
      'Pay via M-Pesa, download instantly',
      'Works in Google Sheets & Excel',
    ],
    cta: 'Browse templates',
    ctaTo: '/shop',
    highlight: false,
  },
];

// ─── Principles ───────────────────────────────────────────────────────────────
const principles = [
  {
    num: '01',
    heading: 'Clarity over complexity.',
    body: 'Every tool we build strips away the noise. You should always know exactly where you stand.',
  },
  {
    num: '02',
    heading: 'Built for action.',
    body: 'Information without direction is useless. Our tools help you make the next move — not just understand the last one.',
  },
  {
    num: '03',
    heading: 'Your money, your pace.',
    body: "We don't tell you how to live. We give you the tools to build the financial life you want, on your own terms.",
  },
];

// ─── Schema ───────────────────────────────────────────────────────────────────
const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'FinInteract',
  url: 'https://fininteract.com',
  logo: 'https://fininteract.com/logo512.png',
  description: 'Practical financial tools for people who take their money seriously.',
  sameAs: [],
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'FinInteract',
  url: 'https://fininteract.com',
};

// ─── Page ─────────────────────────────────────────────────────────────────────
const CompanyHome: React.FC = () => (
  <div className="min-h-screen bg-paper dark:bg-ink-950 flex flex-col">
    <SEO
      title="FinInteract — Financial Tools for People Who Take Their Money Seriously"
      description="Budget, invest, clear debt, and grow your savings — with practical tools built for action. M-Pesa payments. Free to start."
      canonical="/"
      keywords="financial goal planner, budget planner Kenya, debt payoff tracker, investment tracker, compound calculator, M-Pesa financial tools, Budget Planner template, Debt Payoff Planner template, Savings Goal Tracker template, M-Pesa Income Tracker template, Business Bookkeeping template, Freelancer Invoice template, Chama Tracker template, Portfolio Tracker template, Google Sheets financial templates Kenya"
      structuredData={[organizationSchema, websiteSchema]}
    />
    <CompanyHeader />

    <main className="flex-1">

      {/* ── Hero ── */}
      <section className="pt-36 pb-28 max-w-[1080px] mx-auto px-8 sm:px-6">
        <div className="max-w-3xl">
          <motion.p
            className="font-mono text-teal text-[11px] tracking-[0.12em] uppercase mb-7"
            {...fadeUp} transition={{ duration: 0.4 }}
          >
            FinInteract · Financial Tools
          </motion.p>

          <motion.h1
            className="font-serif text-5xl md:text-[64px] text-ink dark:text-paper leading-[1.08] mb-8"
            {...fadeUp} transition={{ duration: 0.4, delay: 0.07 }}
          >
            Every tool you need to{' '}
            <span className="italic text-teal">own your money.</span>
          </motion.h1>

          <motion.p
            className="font-sans font-light text-ink-60 dark:text-ink-20 text-lg leading-relaxed max-w-xl mb-12"
            {...fadeUp} transition={{ duration: 0.4, delay: 0.14 }}
          >
            Budget, track goals, pay off debt, manage investments, and buy
            ready-made spreadsheet templates — all in one place. M-Pesa native.
            Free to start.
          </motion.p>

          <motion.div
            className="flex flex-col sm:flex-row gap-3"
            {...fadeUp} transition={{ duration: 0.4, delay: 0.2 }}
          >
            <Link
              to="/register"
              className="bg-ink text-white px-6 py-3.5 rounded-brand text-sm font-medium hover:bg-ink/90 transition-colors duration-150 flex items-center justify-center gap-2"
            >
              Get started free
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/shop"
              className="border border-ink-20 dark:border-white/10 text-ink-60 dark:text-ink-20 px-6 py-3.5 rounded-brand text-sm font-medium hover:border-teal hover:text-teal transition-all duration-150 text-center flex items-center justify-center gap-2"
            >
              <ShoppingBag className="w-4 h-4" />
              Browse templates
            </Link>
          </motion.div>

          {/* Social proof strip */}
          <motion.div
            className="mt-12 flex flex-wrap items-center gap-6 text-sm text-ink-40 font-light"
            {...fadeUp} transition={{ duration: 0.4, delay: 0.28 }}
          >
            <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-teal" />No credit card required</span>
            <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-teal" />M-Pesa payments</span>
            <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-teal" />iOS & Android app</span>
            <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-teal" />14 spreadsheet templates</span>
          </motion.div>
        </div>
      </section>

      {/* ── Tools ── */}
      <section id="tools" className="bg-white dark:bg-ink border-y border-ink-10 dark:border-white/5 py-24">
        <div className="max-w-[1080px] mx-auto px-8 sm:px-6">
          <motion.div
            className="mb-16"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
          >
            <h2 className="font-serif text-4xl text-ink dark:text-paper mb-3">Everything in one place.</h2>
            <p className="font-sans font-light text-ink-60 dark:text-ink-20 text-base max-w-lg">
              Eight tools. One account. Built for the Kenyan financial reality.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {tools.map((tool, i) => (
              <motion.div
                key={tool.label}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`rounded-brand-lg border p-7 flex flex-col ${
                  !tool.available
                    ? 'bg-white dark:bg-ink border-ink-10 dark:border-white/5 opacity-55'
                    : tool.highlight
                    ? 'bg-ink border-ink dark:border-white/30'
                    : 'bg-paper dark:bg-ink-950 border-ink-10 dark:border-white/5 hover:border-ink-20 dark:hover:border-white/20 transition-colors duration-200'
                }`}
              >
                {/* Top row */}
                <div className="flex items-start justify-between mb-5">
                  <div className={`w-9 h-9 rounded-brand flex items-center justify-center flex-shrink-0 ${
                    tool.highlight ? 'bg-white/10' : 'bg-ink/5 dark:bg-white/5'
                  }`}>
                    <tool.icon className={`w-4.5 h-4.5 ${tool.highlight ? 'text-white' : 'text-ink dark:text-paper'}`} strokeWidth={1.5} />
                  </div>
                  <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-full ${
                    tool.tag === 'Free'        ? 'bg-mint/20 text-ink dark:text-paper'
                    : tool.tag === 'PRO'       ? 'bg-teal/10 text-teal'
                    : tool.tag === 'Shop'      ? 'bg-white/15 text-white'
                    : tool.tag === 'Coming soon' ? 'bg-ink-10 dark:bg-white/10 text-ink-40'
                    : 'bg-teal/10 text-teal'
                  }`}>
                    {tool.tag}
                  </span>
                </div>

                <h3 className={`font-serif text-[18px] mb-2 ${tool.highlight ? 'text-white' : 'text-ink dark:text-paper'}`}>{tool.label}</h3>
                <p className={`font-sans font-light text-sm leading-relaxed mb-5 ${tool.highlight ? 'text-white/70' : 'text-ink-60 dark:text-ink-20'}`}>
                  {tool.description}
                </p>

                <ul className="space-y-1.5 mb-7 flex-1">
                  {tool.features.map(feat => (
                    <li key={feat} className={`flex items-start gap-2 text-[13px] font-light ${tool.highlight ? 'text-white/70' : 'text-ink-60 dark:text-ink-20'}`}>
                      <span className={`w-1 h-1 rounded-full flex-shrink-0 mt-1.5 ${tool.highlight ? 'bg-teal' : 'bg-teal'}`} />
                      {feat}
                    </li>
                  ))}
                </ul>

                {tool.to && tool.cta ? (
                  <Link
                    to={tool.to}
                    className={`inline-flex items-center gap-1.5 text-sm font-medium group transition-colors duration-150 ${
                      tool.highlight
                        ? 'text-teal hover:text-white'
                        : 'text-ink dark:text-paper hover:text-teal'
                    }`}
                  >
                    {tool.cta}
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform duration-150" />
                  </Link>
                ) : (
                  <span className="text-[12px] text-ink-40 font-mono">Notify me when ready →</span>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Template Shop Preview ── */}
      <section className="py-24 max-w-[1080px] mx-auto px-8 sm:px-6">
        <motion.div
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
        >
          <div>
            <p className="font-mono text-teal text-[11px] tracking-[0.12em] uppercase mb-3">Shop · Spreadsheet Templates</p>
            <h2 className="font-serif text-4xl text-ink dark:text-paper mb-3">Ready-made spreadsheets.<br />Pay once via M-Pesa.</h2>
            <p className="font-sans font-light text-ink-60 dark:text-ink-20 text-base max-w-lg">
              Professional Google Sheets templates with real formulas, embedded charts, and KES formatting.
              Download instantly after payment.
            </p>
          </div>
          <Link
            to="/shop"
            className="flex-shrink-0 inline-flex items-center gap-2 bg-teal text-white px-5 py-3 rounded-brand text-sm font-medium hover:opacity-90 transition-opacity duration-150"
          >
            Browse all 14 templates
            <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {shopPreviews.map((t, i) => (
            <MotionLink
              key={t.sku}
              to={`/shop/${t.sku.toLowerCase()}`}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.07 }}
              className="bg-white dark:bg-ink border border-ink-10 dark:border-white/5 rounded-brand-lg p-6 hover:border-ink-20 dark:hover:border-white/20 hover:shadow-sm transition-all duration-200 flex flex-col"
            >
              <span className={`self-start text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full mb-4 ${
                categoryColor[t.category] ?? 'bg-ink/10 dark:bg-white/10 text-ink-60 dark:text-ink-20'
              }`}>
                {t.category}
              </span>
              <h3 className="font-sans text-sm font-medium text-ink dark:text-paper leading-snug mb-4 flex-1">{t.name}</h3>
              <div className="flex items-baseline gap-2 mt-auto">
                <span className="font-sans font-semibold text-ink dark:text-paper text-base">KES {t.price.toLocaleString()}</span>
                <span className="font-sans text-ink-40 text-sm line-through">KES {t.original.toLocaleString()}</span>
              </div>
            </MotionLink>
          ))}
        </div>
      </section>

      {/* ── M-Pesa callout ── */}
      <section className="bg-white dark:bg-ink border-y border-ink-10 dark:border-white/5 py-20">
        <div className="max-w-[1080px] mx-auto px-8 sm:px-6">
          <motion.div
            className="flex flex-col md:flex-row md:items-center gap-10"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
          >
            <div className="w-16 h-16 rounded-brand-lg bg-teal/10 flex items-center justify-center flex-shrink-0">
              <Smartphone className="w-7 h-7 text-teal" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <h2 className="font-serif text-3xl text-ink dark:text-paper mb-3">Card & M-Pesa, from day one.</h2>
              <p className="font-sans font-light text-ink-60 dark:text-ink-20 text-base leading-relaxed max-w-2xl">
                Pay for PRO, buy templates, and log contributions — by card or M-Pesa, whichever's
                easiest. No friction, no waiting on a prompt. We built for how Kenya actually pays.
              </p>
            </div>
            <Link
              to="/register"
              className="flex-shrink-0 inline-flex items-center gap-2 border border-ink-20 dark:border-white/10 text-ink dark:text-paper px-5 py-3 rounded-brand text-sm font-medium hover:border-teal hover:text-teal transition-all duration-150"
            >
              Get started <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section id="pricing" className="py-24 max-w-[1080px] mx-auto px-8 sm:px-6">
        <motion.div
          className="mb-16 text-center"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
        >
          <h2 className="font-serif text-4xl text-ink dark:text-paper mb-3">Simple, honest pricing.</h2>
          <p className="font-sans font-light text-ink-60 dark:text-ink-20 text-base max-w-md mx-auto">
            Start free. Upgrade when you're ready. Buy templates à la carte.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {tiers.map((tier, i) => (
            <motion.div
              key={tier.name}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className={`rounded-brand-lg border p-8 flex flex-col ${
                tier.highlight
                  ? 'bg-ink border-ink dark:border-white/30 text-white'
                  : 'bg-paper dark:bg-ink-950 border-ink-10 dark:border-white/5'
              }`}
            >
              <p className={`font-mono text-[11px] tracking-widest uppercase mb-5 ${tier.highlight ? 'text-teal' : 'text-ink-40'}`}>
                {tier.name}
              </p>
              <div className="mb-2">
                <span className={`font-serif text-3xl ${tier.highlight ? 'text-white' : 'text-ink dark:text-paper'}`}>{tier.price}</span>
              </div>
              <p className={`font-mono text-xs uppercase tracking-wider mb-6 ${tier.highlight ? 'text-white/50' : 'text-ink-40'}`}>
                {tier.period}
              </p>
              <p className={`font-sans font-light text-sm mb-8 leading-relaxed ${tier.highlight ? 'text-white/70' : 'text-ink-60 dark:text-ink-20'}`}>
                {tier.desc}
              </p>
              <ul className="space-y-3 mb-10 flex-1">
                {tier.features.map(feat => (
                  <li key={feat} className={`flex items-start gap-2.5 text-sm font-light ${tier.highlight ? 'text-white/80' : 'text-ink-60 dark:text-ink-20'}`}>
                    <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${tier.highlight ? 'text-teal' : 'text-teal'}`} />
                    {feat}
                  </li>
                ))}
              </ul>
              <Link
                to={tier.ctaTo}
                className={`text-center px-5 py-3 rounded-brand text-sm font-medium transition-all duration-150 ${
                  tier.highlight
                    ? 'bg-teal text-white hover:opacity-90'
                    : 'border border-ink-20 dark:border-white/10 text-ink dark:text-paper hover:border-teal hover:text-teal'
                }`}
              >
                {tier.cta}
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── Principles ── */}
      <section className="bg-white dark:bg-ink border-t border-ink-10 dark:border-white/5 py-24">
        <div className="max-w-[1080px] mx-auto px-8 sm:px-6">
          <motion.div
            className="mb-16"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
          >
            <h2 className="font-serif text-4xl text-ink dark:text-paper mb-3">How we build.</h2>
            <p className="font-sans font-light text-ink-60 dark:text-ink-20 text-base max-w-lg">
              Every tool we ship is guided by three principles.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {principles.map((p, i) => (
              <motion.div
                key={p.num}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
              >
                <p className="font-mono text-teal text-xs tracking-widest mb-5">{p.num}</p>
                <h3 className="font-serif text-xl text-ink dark:text-paper mb-3">{p.heading}</h3>
                <p className="font-sans font-light text-ink-60 dark:text-ink-20 text-[15px] leading-relaxed">{p.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA strip ── */}
      <section className="bg-ink py-20">
        <div className="max-w-[1080px] mx-auto px-8 sm:px-6">
          <motion.div
            className="flex flex-col md:flex-row md:items-center md:justify-between gap-8"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
          >
            <div>
              <h2 className="font-serif text-3xl md:text-4xl text-paper mb-3">
                Start with one tool.<br />
                <span className="italic text-teal">Build from there.</span>
              </h2>
              <p className="font-sans font-light text-paper/60 text-base max-w-md">
                Free account. No card. Cancel PRO anytime. Templates are yours forever.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
              <Link to="/register"
                className="bg-teal text-white px-6 py-3.5 rounded-brand text-sm font-medium hover:opacity-90 transition-opacity duration-150 text-center">
                Create free account
              </Link>
              <Link to="/shop"
                className="border border-paper/30 text-paper px-6 py-3.5 rounded-brand text-sm font-medium hover:border-paper/60 transition-colors duration-150 text-center flex items-center justify-center gap-2">
                <ShoppingBag className="w-4 h-4" />
                Browse templates
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

    </main>

    <Footer company />
  </div>
);

export default CompanyHome;
